import React, { useState, useEffect } from 'react';
import ReactLoading from 'react-loading';
import PrintButton from '../../common/PrintButton';
import Field from '../../fields/Field';
import LocalizedMessage from 'react-l10n';
import { getTranslation } from '../../../lang/main';
import useFormState from '../../FormStateHelper';
import { validateForm } from '../../../selectors/defaultSelectors';
import Numeral from 'numeral';
import FieldNumber from '../../fields/FieldNumber';
import { axiosVerifyAccessCode } from '../../../actions/userActions';
import { createAlert } from '../../../dom/window';

const FORM_LABEL = {
    BusinessName: getTranslation('FIELD.REGISTERED_BUSINESS_NAME'),
    FirstName: getTranslation('FIELD.FIRST_NAME'),
    Surname: getTranslation('FIELD.LAST_NAME'),
    Email: getTranslation('FIELD.EMAIL'),
    EmailConfirmation: getTranslation('FIELD.EMAIL_CONFIRMATION'),
    Phone: getTranslation('FIELD.PHONE'),
    Mobile: getTranslation('FIELD.MOBILE'),
    Fax: getTranslation('FIELD.FAX'),
    Website: getTranslation('FIELD.WEBSITE'),
    AccessKey: 'Access Code',
    AccessCodeToCopyURL: getTranslation('FIELD.ACCESS_CODE_TO_COPY_URL'),
};

function AccountHolderInformationForm(props) {
    const { data, handleFormSubmission, memberType, businessAddress } = props;

    const {
        formData,
        bindUpdateFormData,
        isSubmitting,
        submitForm,
    } = useFormState({
        handleFormSubmission: handleFormSubmission,
        initialFormState: {},
    });
    const [isValidated, setIsValidated] = useState(false);
    const [registrationURLCopied, setregistrationURLCopied] = useState(false);
    const [accessKey, setAccessKey] = useState(null);
    const [loading, setLoading] = useState(false);
    const [accessKeyInvalid, setAccessKeyInvalid] = useState(null);

    const handleAccessKeyChange = (event) => {
        setAccessKey(event.target.value);
    };

    let fieldRefs = {};

    const finalFormData = {
        ...data,
        ...formData,
    };

    const onFormSubmit = (e) => {
        e.preventDefault();

        submitForm(finalFormData);
    };

    const onFieldUpdate = (e) => {
        bindUpdateFormData(e);
    };

    const handleCopyUrl = () => {
        if (accessKey) {
            setLoading(true);
            return axiosVerifyAccessCode(accessKey)
                .then((response) => {
                    console.log(response);
                    let accessCodeVerified =
                        response?.data?.data?.VerifyAccessCode;
                    if (accessCodeVerified) {
                        const baseUrl = `${window.location.protocol}//${window.location.host}`;
                        const locale = window.locale || 'en_HK';
                        const encodedMember = btoa(
                            JSON.stringify({
                                ID: data.ID,
                                BusinessName: data.BusinessName,
                                BusinessAddress:
                                    businessAddress?.FullAddress ?? '',
                            })
                        );

                        const registrationUrl = `${baseUrl}/${locale}?reseller=${encodedMember}`;

                        navigator.clipboard
                            .writeText(registrationUrl)
                            .then(() => {
                                console.log(
                                    'Registration URL copied to clipboard:',
                                    registrationUrl
                                );
                                setregistrationURLCopied(true);
                                setLoading(false);
                            })
                            .catch((err) => {
                                console.error('Could not copy text: ', err);
                                setregistrationURLCopied(false);
                                setLoading(false);
                            });
                        createAlert(
                            {
                                message: 'URL Copied',
                            },
                            'success'
                        );
                        setLoading(false);
                    } else {
                        createAlert(
                            {
                                message: "Access Code Couldn't be verified",
                            },
                            'error'
                        );
                        setLoading(false);
                    }
                })
                .catch((error) => {
                    console.log(error);
                    createAlert(
                        {
                            message: error.message,
                        },
                        'error'
                    );
                    setLoading(false);
                });
        } else {
            createAlert(
                {
                    message: 'Access Code Is Required',
                },
                'error'
            );
        }
    };

    // Effects
    // --------------------
    // - Validate form
    useEffect(() => {
        setIsValidated(!validateForm(fieldRefs));
    }, [finalFormData]);

    return (
        <>
            <React.Fragment>
                <div className='card__header'>
                    <span className='card__header-wrapper'>
                        <span className='card__header-icon'>
                            <span className='icon icon-user-details' />
                        </span>
                    </span>
                    <h3 className='heading heading--lv4'>
                        <LocalizedMessage
                            id={'ACCOUNT_HOLDER_INFORMATION_FORM.HEADING'}
                        />
                    </h3>
                </div>
                <form className='form' onSubmit={onFormSubmit}>
                    <div className='row'>
                        <div className='col-xs-12 col-md-5 col-md-push-7 vspace vspace--small'>
                            <div className='customer-tag__wrapper'>
                                {data.ID != '' && data.ID != null && (
                                    <div className='customer-tag__container'>
                                        <div className='customer-tag'>
                                            <div className='customer-tag__icon'>
                                                <span className='icon icon-client-number' />
                                            </div>
                                            <div className='customer-tag__heading'>
                                                <LocalizedMessage
                                                    id={
                                                        'ACCOUNT_HOLDER_INFORMATION_FORM.CUSTOMER_ID'
                                                    }
                                                />
                                            </div>
                                            <div className='customer-tag__number'>
                                                {Numeral(data.ID).format(
                                                    '0000000'
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {data.EXOAccountID?.trim() && (
                                    <div className='customer-tag__container'>
                                        <div className='customer-tag'>
                                            <div className='customer-tag__icon'>
                                                <span className='icon icon-client-number' />
                                            </div>
                                            <div className='customer-tag__heading'>
                                                <LocalizedMessage
                                                    id={
                                                        'ACCOUNT_HOLDER_INFORMATION_FORM.CUSTOMER_EXO_ID'
                                                    }
                                                />
                                            </div>
                                            <div className='customer-tag__number'>
                                                {data.EXOAccountID}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {data.CreateSubStoreAccessCode != '' &&
                                    data.CreateSubStoreAccessCode != null && (
                                        <div className='customer-tag__container'>
                                            <div className='customer-tag'>
                                                <div className='customer-tag__icon'>
                                                    <span className='icon icon-cog' />
                                                </div>
                                                <div className='customer-tag__heading'>
                                                    <LocalizedMessage
                                                        id={
                                                            'ACCOUNT_HOLDER_INFORMATION_FORM.CUSTOM_WEBSTORE_CODE'
                                                        }
                                                    />
                                                </div>
                                                <div className='customer-tag__number'>
                                                    {
                                                        data.CreateSubStoreAccessCode
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                {memberType == 'reseller' && (
                                    <div style={{ marginTop: '35px' }}>
                                        <Field
                                            label={
                                                accessKeyInvalid ||
                                                FORM_LABEL['AccessKey']
                                            }
                                            animatedLabel={
                                                accessKeyInvalid ||
                                                FORM_LABEL['AccessKey']
                                            }
                                            name='AccessKey'
                                            value={accessKey || ''}
                                            onChange={handleAccessKeyChange}
                                        />
                                        <button
                                            className='button button--grey button--small button--thin hidden-xs hidden-sm'
                                            type='button'
                                            onClick={handleCopyUrl}
                                        >
                                            {loading ? (
                                                <ReactLoading
                                                    type='spin'
                                                    color='black'
                                                    height={20}
                                                    width={20}
                                                />
                                            ) : (
                                                <>
                                                    <span className='icon icon-copy' />
                                                    <span>
                                                        <LocalizedMessage
                                                            id={
                                                                'COPY_REGISTRATION_URL'
                                                            }
                                                        />
                                                    </span>
                                                </>
                                            )}
                                        </button>
                                        <PrintButton />
                                    </div>
                                )}

                                {memberType != 'reseller' && <PrintButton />}
                            </div>
                        </div>
                        <div className='col-xs-12 col-md-7 col-md-pull-5 vspace vspace--small'>
                            <Field
                                required
                                ref={(r) => (fieldRefs.BusinessName = r)}
                                label={FORM_LABEL['BusinessName']}
                                animatedLabel={FORM_LABEL['BusinessName']}
                                name='BusinessName'
                                value={finalFormData.BusinessName || ''}
                                onChange={onFieldUpdate}
                            />
                            <div className='row row--small'>
                                <div className='col-xs-12 col-sm-6'>
                                    <Field
                                        required
                                        ref={(r) => (fieldRefs.FirstName = r)}
                                        label={FORM_LABEL['FirstName']}
                                        animatedLabel={FORM_LABEL['FirstName']}
                                        name='FirstName'
                                        value={finalFormData.FirstName || ''}
                                        onChange={onFieldUpdate}
                                    />
                                </div>
                                <div className='col-xs-12 col-sm-6'>
                                    <Field
                                        required
                                        ref={(r) => (fieldRefs.Surname = r)}
                                        label={FORM_LABEL['Surname']}
                                        animatedLabel={FORM_LABEL['Surname']}
                                        name='Surname'
                                        value={finalFormData.Surname || ''}
                                        onChange={onFieldUpdate}
                                    />
                                </div>
                            </div>
                            <div className='row row--small'>
                                <div className='col-xs-12 col-sm-6'>
                                    <FieldNumber
                                        required
                                        ref={(r) => (fieldRefs.Phone = r)}
                                        label={FORM_LABEL['Phone']}
                                        animatedLabel={FORM_LABEL['Phone']}
                                        name='Phone'
                                        value={finalFormData.Phone || ''}
                                        onChange={onFieldUpdate}
                                    />
                                </div>
                                <div className='col-xs-12 col-sm-6'>
                                    <FieldNumber
                                        ref={(r) => (fieldRefs.Mobile = r)}
                                        label={FORM_LABEL['Mobile']}
                                        animatedLabel={FORM_LABEL['Mobile']}
                                        name='Mobile'
                                        type='number'
                                        value={finalFormData.Mobile || ''}
                                        onChange={onFieldUpdate}
                                    />
                                </div>
                            </div>
                            <div className='row row--small'>
                                <div className='col-xs-12 col-sm-6'>
                                    <Field
                                        ref={(r) => (fieldRefs.Fax = r)}
                                        label={FORM_LABEL['Fax']}
                                        animatedLabel={FORM_LABEL['Fax']}
                                        name='Fax'
                                        value={finalFormData.Fax || ''}
                                        onChange={onFieldUpdate}
                                    />
                                </div>
                                <div className='col-xs-12 col-sm-6'>
                                    <Field
                                        ref={(r) => (fieldRefs.Website = r)}
                                        label={FORM_LABEL['Website']}
                                        animatedLabel={FORM_LABEL['Website']}
                                        name='Website'
                                        value={finalFormData.Website || ''}
                                        onChange={onFieldUpdate}
                                    />
                                </div>
                            </div>

                            {memberType == 'wholesaler' && (
                                <Field
                                    ref={(r) =>
                                        (fieldRefs.AccessCodeToCopyURL = r)
                                    }
                                    label={FORM_LABEL['AccessCodeToCopyURL']}
                                    animatedLabel={
                                        FORM_LABEL['AccessCodeToCopyURL']
                                    }
                                    name='AccessCodeToCopyURL'
                                    value={
                                        finalFormData.AccessCodeToCopyURL || ''
                                    }
                                    onChange={onFieldUpdate}
                                />
                            )}

                            <button
                                type='submit'
                                className={`form__submit button ${
                                    !isValidated ? 'button--disabled' : ''
                                }`}
                                disabled={!isValidated}
                            >
                                <span
                                    style={{
                                        opacity: isSubmitting === true ? 0 : 1,
                                    }}
                                >
                                    <LocalizedMessage id={'SAVE'} />
                                </span>
                                {isSubmitting === true && (
                                    <ReactLoading
                                        className='button__spinner'
                                        type='spin'
                                        color='white'
                                        height={20}
                                        width={20}
                                    />
                                )}
                            </button>
                        </div>
                    </div>
                </form>
            </React.Fragment>
        </>
    );
}

export default AccountHolderInformationForm;
